/**
 * The files of one request, from the moment they are picked to the moment each has
 * landed or been refused — pure, so `RequestComposer.tsx` holds a list and nothing
 * else, and every change to it is one of these.
 *
 * **A file is sent after its request exists, one at a time.** `POST …/requests/:ref/
 * attachments` needs the reference, and the route writes a row per file against one
 * work order; one at a time also means a client watching a 2 GB video go up is told
 * about one failure, not four at once.
 *
 * **A file that fails does not undo the request, and is not sent twice.** Each one
 * keeps its own state, so *Try again* re-sends the ones that failed and nothing else —
 * the old composer's trap was a form whose only button drafted a second request and
 * re-sent the files that had already arrived.
 */

/** One file picked for a request, under a key of its own: two files may share a name. */
export type PickedFile = { readonly key: string; readonly file: File }

export type FileState = 'waiting' | 'sending' | 'attached' | 'failed'

/** One file on its way to a request, and how far it has got. */
export type SentFile = PickedFile & {
  readonly state: FileState
  /** Bytes sent so far. The file's whole size once it is `attached`. */
  readonly sent: number
  /** What the portal said, for a file that `failed`. */
  readonly reason: string | null
}

let picks = 0

/** The files a person just chose, each with a key no other pick has had. */
export function picked(files: readonly File[]): PickedFile[] {
  return files.map((file) => {
    picks += 1
    return { key: `file-${String(picks)}`, file }
  })
}

/** Picked files, queued to be sent. */
export function queued(files: readonly PickedFile[]): SentFile[] {
  return files.map((one) => ({ ...one, state: 'waiting', sent: 0, reason: null }))
}

/** The list with one file changed. */
export function changed(
  files: readonly SentFile[],
  key: string,
  change: Partial<Pick<SentFile, 'state' | 'sent' | 'reason'>>,
): SentFile[] {
  return files.map((one) => (one.key === key ? { ...one, ...change } : one))
}

/** The failed files, queued again — and only those. */
export function retried(files: readonly SentFile[]): SentFile[] {
  return files.map((one) =>
    one.state === 'failed' ? { ...one, state: 'waiting', sent: 0, reason: null } : one,
  )
}

/** Whether any file is still to go or going. */
export function isGoing(files: readonly SentFile[]): boolean {
  return files.some((one) => one.state === 'waiting' || one.state === 'sending')
}

/** The whole batch as one figure: bytes sent of bytes to send, and how many are left. */
export type Progress = {
  readonly sent: number
  readonly total: number
  /** Files not yet attached or failed. */
  readonly left: number
  readonly failed: number
}

export function progressOf(files: readonly SentFile[]): Progress {
  let sent = 0
  let total = 0
  let left = 0
  let failed = 0
  for (const one of files) {
    total += one.file.size
    sent += Math.min(one.sent, one.file.size)
    if (one.state === 'waiting' || one.state === 'sending') left += 1
    if (one.state === 'failed') failed += 1
  }
  return { sent, total, left, failed }
}
